import { Layout, Modal, UI, Utils } from 'common';
import React, { useContext, useEffect, useState } from 'react';
import AccountCommunication from '../utils/AccountCommunication';
import { MissingFieldMessages } from './ChangePassword.enum';

const { LayoutContext } = Layout;
const { ModalContext, ModalButtonActionType } = Modal;
const {
	WrappedInputTextWithLabel,
	WrappedButton,
	WrappedButtonType,
	SaveConfirmation,
} = UI;
const { Logger, PasswordValidation, Session, Validation } = Utils;

export const ChangePassword = () => {
	const [oldPassword, setOldPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmNewPassword, setConfirmNewPassword] = useState('');
	const [saved, setSaved] = useState(false);
	const [clean, setClean] = useState(true);
	const [validations, setValidations] = useState({
		// 0 = untouched, -1 = failed, 1 = valid
		oldPassword: 0,
		newPassword: 0,
		confirmNewPassword: 0,
	});
	const [errorMessages, setErrorMessages] = useState({
		oldPassword: '',
		newPassword: '',
		confirmNewPassword: '',
	});

	const { isMobile } = useContext(LayoutContext);
	const { showError } = useContext(ModalContext);

	useEffect(() => {
		Session.setFormStateDirty(false);
	}, []);

	const onFieldChange = ({ target: { name, value } }) => {
		setSaved(false);
		setClean(false);

		if (name === 'oldPassword') {
			setOldPassword(value);
		} else if (name === 'newPassword') {
			setNewPassword(value);
		} else if (name === 'confirmNewPassword') {
			setConfirmNewPassword(value);
		}

		Session.setFormStateDirty(true);
	};

	const triggerValidation = (onSubmit = false, onBlurField) => {
		const { customerDetails = {} } = Session.getAccountInfo();
		const username = customerDetails.username || '';
		const newValidations = { ...validations };
		const newErrorMessages = { ...errorMessages };
		const checkField = {
			oldPassword,
			newPassword,
			confirmNewPassword,
		};
		const { PASSWORD_RULES } = PasswordValidation;
		let validated = true;

		Object.keys(validations).forEach(name => {
			const fieldValue = checkField[name];

			// validate all fields for onSubmit event, only validate focused field for onBlur event
			if (onSubmit || name === onBlurField) {
				newValidations[name] = fieldValue !== '' ? 1 : -1;
				newErrorMessages[name] =
					fieldValue !== '' ? '' : MissingFieldMessages[name];

				if (
					(name === 'newPassword' || name === 'confirmNewPassword') &&
					validations.confirmNewPassword !== 0
				) {
					newValidations.confirmNewPassword =
						newPassword === confirmNewPassword ? 1 : -1;
					newErrorMessages.confirmNewPassword =
						newPassword === confirmNewPassword ? '' : 'Passwords do not match.';
				}

				if (
					name === 'oldPassword' &&
					oldPassword.length > PASSWORD_RULES.MAX_LENGTH
				) {
					newValidations.oldPassword = -1;
					newErrorMessages.oldPassword = `Current Password cannot exceed ${PASSWORD_RULES.MAX_LENGTH} characters.`;
				}

				if (name === 'newPassword' && newPassword.length > 0) {
					const passwordError = PasswordValidation.getPasswordErrorMessage(
						newPassword,
						username
					);
					if (passwordError) {
						newValidations.newPassword = -1;
						newErrorMessages.newPassword = passwordError;
					}
				}
			}
		});

		if (Object.values(newValidations).includes(-1)) {
			validated = false;
		}

		setValidations(newValidations);
		setErrorMessages(newErrorMessages);

		if (!validated && onSubmit) {
			showError({
				data: {
					title: 'Incomplete Fields',
					description:
						Validation.buildValidationErrorDescription(newErrorMessages),
					primaryButtonType: ModalButtonActionType.Ok,
					primaryButtonText: 'Close',
					handleType: 'custom',
				},
			});

			return false;
		}

		return true;
	};

	const onFieldBlur = ({ target: { name } }) => {
		triggerValidation(false, name);
	};

	const createNewPassword = async e => {
		e.preventDefault();

		Logger.saveActionData('Change Password Button Clicked');

		// validate all fields and prevent form submission if any fields are invalid
		if (!triggerValidation(true)) {
			return;
		}

		const data = {
			oldPassword,
			newPassword,
		};

		const response = await AccountCommunication.changePassword(data);
		if (AccountCommunication.isSuccessful(response)) {
			setOldPassword('');
			setNewPassword('');
			setConfirmNewPassword('');
			setSaved(true);
			setClean(true);

			Session.setFormStateDirty(false);

			Logger.logToServer({ category: 'Change Password' });
		} else {
			showError({ response });
		}
	};

	const mobileCls = isMobile ? ' mobile-wrapper' : '';

	return (
		<div className="account-change-password-container">
			<div className="account-title">
				Change Password
				{saved && <SaveConfirmation success text="Changes Saved" />}
			</div>
			<div className="password-form">
				<form onSubmit={createNewPassword}>
					<div className="p-grid p-nogutter p-dir-col">
						<WrappedInputTextWithLabel
							showErrorBelow={isMobile}
							isInputInvalid={validations.oldPassword === -1}
							errorMessage={errorMessages.oldPassword}
							className={`p-col-12 ${mobileCls}`}
							label="Current Password:"
							uiElementConfig={{
								type: 'password',
								onBlur: onFieldBlur,
								onChange: onFieldChange,
								value: oldPassword,
								dataqa: 'currentpassword',
								name: 'oldPassword',
							}}
						/>
						<WrappedInputTextWithLabel
							showErrorBelow={isMobile}
							isInputInvalid={validations.newPassword === -1}
							errorMessage={errorMessages.newPassword}
							className={`p-col-12 ${mobileCls}`}
							label="New Password:"
							uiElementConfig={{
								type: 'password',
								onBlur: onFieldBlur,
								onChange: onFieldChange,
								value: newPassword,
								dataqa: 'newpassword',
								name: 'newPassword',
							}}
						/>
						<WrappedInputTextWithLabel
							showErrorBelow={isMobile}
							isInputInvalid={validations.confirmNewPassword === -1}
							errorMessage={errorMessages.confirmNewPassword}
							className={`p-col-12 ${mobileCls}`}
							label="Confirm New Password:"
							uiElementConfig={{
								type: 'password',
								onBlur: onFieldBlur,
								onChange: onFieldChange,
								value: confirmNewPassword,
								dataqa: 'confirmnewpassword',
								name: 'confirmNewPassword',
							}}
						/>
					</div>
					<div className={`requirements-container${mobileCls}`}>
						<div className="requirements">Password Requirements:</div>
						<ul>
							<li>At least 15 characters</li>
							<li>At least 1 letter</li>
							<li>At least 1 number</li>
							<li>At least 1 special character (!@#$%^&*)</li>
							<li>Not include your username</li>
							<li>Not be a previously-used password</li>
						</ul>
					</div>
					<WrappedButton
						className={`save-password-button${mobileCls}`}
						label="Save"
						type="submit"
						buttonType={WrappedButtonType.Primary}
						dataqa="save-change-password"
						disabled={clean}
					/>
				</form>
			</div>
		</div>
	);
};
